import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react"
import { type UserAccountData, getHighResTwitterImage } from "src/utils/helper"
import type { UserProfileData } from "@/helpers/user-profile"
import { getUserProfileDataApi } from "src/utils/api"
import { useChainId, useSwitchChain } from "wagmi"
import { useChain } from "@azuro-org/sdk"

type UserContextType = {
  currentUser: Partial<UserAccountData> | null
  profileData: UserProfileData | null
  isLoginComplete: boolean
  loginMethod: string | null
  loginUser: (userData: any) => void
  setReferralStatistics: (stats: {
    betCount: string
    betVolume: string
    userCount: string
  }) => void
  setLoginCompleted: (completed: boolean) => void
  logoutUser: () => void
  isUserConnecting: boolean
  setUserConnecting: () => void
  fetchProfileData: (walletAddress: string) => Promise<void>
  isProfileLoading: boolean
  setLoginMethod: (method: string | null) => void
  userProfile: UserProfile | null
  setUserProfile: (profile: UserProfile | null) => void
}

interface UserProfile {
  id: string
  wallet_address: string
  email?: string
  first_name?: string
  last_name?: string
  is_verified?: boolean
  [key: string]: unknown
}

const UserContext = createContext<UserContextType | undefined>(undefined)

export const useUserContext = (): UserContextType => {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error("useUserContext must be used within a UserProvider")
  }
  return context
}

const getInitialState = (): Partial<UserAccountData> | null => {
  if (typeof window !== "undefined") {
    const currentUser = localStorage.getItem("currentUser")
    return currentUser
      ? (JSON.parse(currentUser) as Partial<UserAccountData>)
      : null
  }
  return null
}

const getInitialLoginComplete = (): boolean => {
  if (typeof window !== "undefined") {
    const isLoginComplete = localStorage.getItem("isLoginComplete")
    return isLoginComplete ? (JSON.parse(isLoginComplete) as boolean) : false
  }
  return false
}

export const UserProvider: React.CFC = ({ children }) => {
  const chainId = useChainId()
  const { switchChain } = useSwitchChain()
  const { setAppChainId } = useChain()
  const [currentUser, setCurrentUser] =
    useState<Partial<UserAccountData> | null>(getInitialState)
  const [profileData, setProfileData] = useState<UserProfileData | null>(null)
  const [isProfileLoading, setIsProfileLoading] = useState(false)
  const [loginMethod, setLoginMethod] = useState<string | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)

  const [isLoginComplete, setIsLoginCompleted] = useState<boolean>(
    getInitialLoginComplete
  )

  const [isUserConnecting, setIsUserConnecting] = useState<boolean>(false)

  useEffect(() => {
    localStorage.setItem("currentUser", JSON.stringify(currentUser))
  }, [currentUser])

  useEffect(() => {
    localStorage.setItem("isLoginComplete", JSON.stringify(isLoginComplete))
  }, [isLoginComplete])

  const fetchProfileData = async (walletAddress: string) => {
    if (!walletAddress) {
      return
    }

    try {
      setIsProfileLoading(true)
      const {
        data: { user },
      } = await getUserProfileDataApi({
        wallet_address: walletAddress,
      })
      if (user) {
        setProfileData({
          ...user,
          profile_image: getHighResTwitterImage(user?.profile_image),
        })
      }
    } catch (error) {
      console.error("Error fetching profile data:", error)
    } finally {
      setIsProfileLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser?.wallet_address) {
      fetchProfileData(currentUser.wallet_address)
    }
  }, [currentUser?.wallet_address])

  const setLoginCompleted = (completed: boolean) => {
    setIsUserConnecting(false)
    setIsLoginCompleted(completed)
  }

  const setUserConnecting = () => {
    setIsUserConnecting(true)
  }

  const loginUser = (userData: Partial<UserAccountData>) => {
    setCurrentUser(userData)
  }

  const setReferralStatistics = (stats: {
    betCount: string
    betVolume: string
    userCount: string
  }) => {
    setCurrentUser(prevState => ({
      ...prevState,
      referralStatistics: stats,
    }))
  }

  const logoutUser = () => {
    setLoginMethod(null)
    setCurrentUser(null)
    setProfileData(null)
    setLoginCompleted(false)
    // Reset loading states on logout
    setIsUserConnecting(false)
    // Remove all bonus-<id> keys from localStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("bonus-")) {
        localStorage.removeItem(key)
      }
    })
  }

  // Add effect to reset isUserConnecting if it stays true for too long (safety mechanism)
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (isUserConnecting) {
      // If user is connecting for more than 60 seconds, reset the state
      timeoutId = setTimeout(() => {
        console.warn("User connecting state timed out, resetting...")
        setIsUserConnecting(false)
      }, 60000) // 60 seconds timeout
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isUserConnecting])

  const handleSetLoginMethod = useCallback((method: string | null) => {
    setLoginMethod(method)
  }, [])

  const handleSetUserProfile = useCallback((profile: UserProfile | null) => {
    setUserProfile(profile)
  }, [])

  const value: UserContextType = {
    currentUser,
    profileData,
    loginUser,
    setReferralStatistics,
    logoutUser,
    isLoginComplete,
    setLoginCompleted,
    isUserConnecting,
    setUserConnecting,
    fetchProfileData,
    isProfileLoading,
    loginMethod,
    setLoginMethod: handleSetLoginMethod,
    userProfile,
    setUserProfile: handleSetUserProfile,
  }

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>
}
